import { apiClient, getPlatformUrl, pollTask } from "../../lib/api";
import {
  NodeNamesMap,
  RcaContext,
  GcmModel,
  DistributionsDef,
  AnomalyRangeDef,
  GcmAttributionDef,
  FitCausalMechanismDef,
  RcaDistributionsReq,
  RcaTrendReq,
  RcaTrendDef,
  RcaAnomalyScoreReq,
  RcaAnomalyRangeReq,
  GcmUpstreamInfluenceReq,
  GcmAnomalyAttributionReq,
  GcmDistributionChangeReq,
  RenameGcmReq,
} from "./type/rca-def";

export class RcaGateway {
  async fetchNodeNamesMap(asset_id: string): Promise<NodeNamesMap> {
    const url = `${getPlatformUrl()}/xcausal/helper/node-names-map?asset_id=${asset_id}`;
    return await apiClient.get({ url }) as NodeNamesMap;
  }

  async fetchContext(asset_id: string): Promise<RcaContext> {
    const url = `${getPlatformUrl()}/xcausal/rca/context?asset_id=${asset_id}`;
    return await apiClient.get({ url }) as RcaContext;
  }

  async listGcm(asset_id: string): Promise<GcmModel[]> {
    const url = `${getPlatformUrl()}/xcausal/rca/list-gcm?asset_id=${asset_id}`;
    return await apiClient.get({ url }) as GcmModel[];
  }

  async fetchDistributions(req: RcaDistributionsReq): Promise<DistributionsDef> {
    const query = new URLSearchParams(req as any).toString();
    const url = `${getPlatformUrl()}/xcausal/rca/distributions?${query}`;
    // 描画不可データ（1点・全同値）は 400 で throw される（apiClient がエラー化）→ 呼び出し側で捕捉
    return await apiClient.get({ url }) as DistributionsDef;
  }

  async fetchTrend(req: RcaTrendReq): Promise<RcaTrendDef> {
    // freq は任意（未指定なら BE が自動粒度）。undefined は URLSearchParams に載せない
    const params: Record<string, string> = {
      asset_id: req.asset_id,
      target_node: req.target_node,
    };
    if (req.freq) params.freq = req.freq;
    const query = new URLSearchParams(params).toString();
    const url = `${getPlatformUrl()}/xcausal/rca/trend?${query}`;
    return await apiClient.get({ url }) as RcaTrendDef;
  }

  async fetchAnomalyScore(req: RcaAnomalyScoreReq): Promise<string> {
    const query = new URLSearchParams(req as any).toString();
    const url = `${getPlatformUrl()}/xcausal/rca/anomaly-score?${query}`;
    return await apiClient.get({ url }) as string;
  }

  async fetchAnomalyRange(req: RcaAnomalyRangeReq): Promise<AnomalyRangeDef> {
    const query = new URLSearchParams(req as any).toString();
    const url = `${getPlatformUrl()}/xcausal/rca/anomaly-range?${query}`;
    return await apiClient.get({ url }) as AnomalyRangeDef;
  }

  async fitCausalMechanism(asset_id: string): Promise<FitCausalMechanismDef> {
    // message-queue（非同期）: POST → task_id → status ポーリング → result（gcm_id を含む）
    const url = `${getPlatformUrl()}/api/message-queue/rca/fit-causal-mechanism`;
    const res: any = await apiClient.post({ url, body: { asset_id }, useToken: true });
    const taskId = res?.task_id ?? res?.id;
    if (taskId) {
      return await pollTask<FitCausalMechanismDef>(taskId);
    }
    return res as FitCausalMechanismDef;
  }

  async getUpstreamInfluence(req: GcmUpstreamInfluenceReq): Promise<GcmAttributionDef> {
    const url = `${getPlatformUrl()}/api/message-queue/rca/gcm/upstream-influence`;
    const res: any = await apiClient.post({ url, body: req, useToken: true });
    const taskId = res?.task_id ?? res?.id;
    if (taskId) {
      return await pollTask<GcmAttributionDef>(taskId);
    }
    return res as GcmAttributionDef;
  }

  async getAnomalyAttribution(req: GcmAnomalyAttributionReq): Promise<GcmAttributionDef> {
    const url = `${getPlatformUrl()}/api/message-queue/rca/gcm/anomaly-attribution`;
    const res: any = await apiClient.post({ url, body: req, useToken: true });
    const taskId = res?.task_id ?? res?.id;
    if (taskId) {
      return await pollTask<GcmAttributionDef>(taskId);
    }
    return res as GcmAttributionDef;
  }

  // ===== POST のみ（task_id を返す）。UI側で永続化＋ポーリングする用 =====
  async postFitCausalMechanism(asset_id: string, mode?: "advanced"): Promise<{ task_id: string }> {
    const url = `${getPlatformUrl()}/api/message-queue/rca/fit-causal-mechanism`;
    // mode 指定時のみ body に含める（未指定は従来どおり asset_id のみ）
    const body = mode ? { asset_id, mode } : { asset_id };
    const res: any = await apiClient.post({ url, body, useToken: true });
    return { task_id: res?.task_id ?? res?.id };
  }

  async postUpstreamInfluence(req: GcmUpstreamInfluenceReq): Promise<{ task_id: string }> {
    const url = `${getPlatformUrl()}/api/message-queue/rca/gcm/upstream-influence`;
    const res: any = await apiClient.post({ url, body: req, useToken: true });
    return { task_id: res?.task_id ?? res?.id };
  }

  async postAnomalyAttribution(req: GcmAnomalyAttributionReq): Promise<{ task_id: string }> {
    const url = `${getPlatformUrl()}/api/message-queue/rca/gcm/anomaly-attribution`;
    const res: any = await apiClient.post({ url, body: req, useToken: true });
    return { task_id: res?.task_id ?? res?.id };
  }

  async getDistributionChange(req: GcmDistributionChangeReq): Promise<GcmAttributionDef> {
    const url = `${getPlatformUrl()}/xcausal/rca/gcm/distribution-change`;
    return await apiClient.post({ url, body: req }) as GcmAttributionDef;
  }

  async renameGcm(gcm_id: string, req: RenameGcmReq): Promise<string> {
    const url = `${getPlatformUrl()}/xcausal/rca/gcm/${gcm_id}/rename`;
    return await apiClient.update({ url, body: req }) as string;
  }

  async deleteGcm(gcm_id: string): Promise<string> {
    const url = `${getPlatformUrl()}/xcausal/rca/gcm/${gcm_id}`;
    return await apiClient.remove({ url }) as string;
  }
}
